import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ComparisonChange, ComparisonSummary } from '@/lib/registration-comparison'

const directions = {
  up: { icon: ArrowUp, label: 'Up', color: 'text-success' },
  down: { icon: ArrowDown, label: 'Down', color: 'text-destructive' },
  level: { icon: Minus, label: 'Level', color: 'text-muted-foreground' },
}

export function RegistrationChange({ change }: { change: ComparisonChange }) {
  const direction = change.direction ? directions[change.direction] : null
  return (
    <span className={cn('inline-flex items-center gap-1 tabular-nums', direction?.color)}>
      {direction && (
        <direction.icon
          aria-label={direction.label}
          role="img"
          className="size-3.5"
          strokeWidth={1.5}
        />
      )}
      {change.badge && <Badge variant="neutral">{change.badge}</Badge>}
      {change.label}
    </span>
  )
}

export function RegistrationSummary({ figures }: { figures: ComparisonSummary[] }) {
  return (
    <section
      aria-label="Registration summary"
      className={cn(
        'mt-4 grid divide-x divide-border rounded-md border border-border bg-card',
        figures.length === 1 && 'grid-cols-1 sm:max-w-xs',
        figures.length === 3 && 'grid-cols-2 sm:grid-cols-3',
        figures.length === 4 && 'grid-cols-2 sm:grid-cols-4',
      )}
    >
      {figures.map((figure) => (
        <div key={figure.label} className="px-4 py-3">
          <p className="whitespace-nowrap text-caption text-muted-foreground">{figure.label}</p>
          <div className="mt-0.5 flex items-baseline gap-2 text-heading-sm font-semibold tabular-nums">
            {figure.change ? <RegistrationChange change={figure.change} /> : figure.value}
            {figure.qualifier && (
              <span className="text-caption font-normal text-muted-foreground">
                {figure.qualifier}
              </span>
            )}
          </div>
        </div>
      ))}
    </section>
  )
}
